"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { useActiveCommandAgent } from "@/hooks/use-active-command-agent";
import { useCommandPanelThread } from "@/hooks/use-command-panel-thread";
import { isCommandPanelDisabledRoute } from "@/utils/command-panel-routes";

type CommandPanelThread = ReturnType<typeof useCommandPanelThread>;

interface CommandPanelContextValue {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
    activeAgent: ReturnType<typeof useActiveCommandAgent>["activeAgent"];
    isLoadingActiveAgent: boolean;
    canListAgents: boolean;
    thread: CommandPanelThread;
}

const CommandPanelContext = createContext<CommandPanelContextValue | null>(null);
const OPEN_STORAGE_KEY = "aios:command-sidebar-open";

function isLegacyCommandTeamRoute(pathname: string | null): boolean {
    if (!pathname) return false;
    return pathname.startsWith("/workforce/command-team-old");
}

export function useCommandPanel(): CommandPanelContextValue {
    const ctx = useContext(CommandPanelContext);
    if (!ctx) throw new Error("useCommandPanel must be used within a CommandPanelProvider");
    return ctx;
}

export function CommandPanelProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const pathname = usePathname();
    const { activeAgent, isLoadingActiveAgent, canListAgents } = useActiveCommandAgent();
    const thread = useCommandPanelThread(activeAgent?.id ?? null);
    const restored = useRef(false);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

    useEffect(() => {
        try {
            if (sessionStorage.getItem(OPEN_STORAGE_KEY) === "true") setIsOpen(true);
        } catch {
            /* storage unavailable */
        }
        restored.current = true;
    }, []);

    useEffect(() => {
        if (!restored.current) return;
        try {
            sessionStorage.setItem(OPEN_STORAGE_KEY, isOpen ? "true" : "false");
        } catch {
            /* ignore */
        }
    }, [isOpen]);

    useEffect(() => {
        if (isCommandPanelDisabledRoute(pathname) || isLegacyCommandTeamRoute(pathname)) {
            setIsOpen(false);
        }
    }, [pathname]);

    useEffect(() => {
        document.documentElement.setAttribute("data-command-panel-open", String(isOpen));
        return () => {
            document.documentElement.removeAttribute("data-command-panel-open");
        };
    }, [isOpen]);

    useEffect(() => {
        const body = document.body;
        if (!isOpen) {
            delete body.dataset.commandOpen;
            body.style.removeProperty("--command-top");
            return;
        }
        body.dataset.commandOpen = "true";

        const findHeader = (): HTMLElement | null => {
            const wrapper = document.querySelector(".group\\/sidebar-wrapper");
            const scope: ParentNode = wrapper ?? document;
            const headers = Array.from(scope.querySelectorAll<HTMLElement>("header"));
            if (headers.length === 0) return null;
            const atTop = headers.filter((header) => {
                const rect = header.getBoundingClientRect();
                return rect.top <= 8 && rect.height > 0 && rect.width > 120;
            });
            const pool = atTop.length > 0 ? atTop : headers;
            return pool.reduce((best, header) =>
                header.getBoundingClientRect().bottom > best.getBoundingClientRect().bottom
                    ? header
                    : best
            );
        };

        let header = findHeader();
        const measure = () => {
            header = findHeader() ?? header;
            const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
            body.style.setProperty("--command-top", `${Math.max(0, bottom)}px`);
        };
        measure();
        const raf = requestAnimationFrame(measure);

        let resizeObserver: ResizeObserver | undefined;
        if (header && "ResizeObserver" in window) {
            resizeObserver = new ResizeObserver(measure);
            resizeObserver.observe(header);
        }
        window.addEventListener("resize", measure);
        return () => {
            cancelAnimationFrame(raf);
            resizeObserver?.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [isOpen]);

    const value = useMemo<CommandPanelContextValue>(
        () => ({ isOpen, open, close, toggle, activeAgent, isLoadingActiveAgent, canListAgents, thread }),
        [isOpen, open, close, toggle, activeAgent, isLoadingActiveAgent, canListAgents, thread]
    );

    return <CommandPanelContext.Provider value={value}>{children}</CommandPanelContext.Provider>;
}
